/**
 * Georreferência dos PNGs de radar do SIPAM.
 *
 * O PNG NÃO é uma grade lon/lat, e o quadrado da camada WFS
 * `sipam:sipam_cobertura_radar` NÃO é o quadro da imagem — é o bbox do
 * CÍRCULO de cobertura (raio 2,176°, 200 vértices). Levantamento de
 * 2026-09-21 (ajuste contra os polígonos de tempestade que o próprio SIPAM
 * publica para o mesmo instante de cada PNG — camadas `SIPAM_STORM` e
 * `SIPAM_STORM_H5` —, 48 a 241 tempestades por radar):
 *
 * - o PNG é uma grade AZIMUTAL EQUIDISTANTE (AEQD) centrada no radar;
 * - 954 px (oito radares): esfera R = 6371 km, pixel de 502,05 m, meia
 *   largura de 239,48 km. O buraco sem dado de 4×4 px da antena fica
 *   exatamente no pixel (477, 477), e o círculo de alcance é inscrito;
 * - 1000 px (Belém, outro sistema): elipsoide WGS84, pixel de 500 m, 250 km
 *   de alcance;
 * - resíduo do modelo: 0,5–0,8 px rms. O bbox da WFS erraria até ~5 px
 *   (2,5 km) nos radares de 954 px e ~20 px (10 km) em Belém.
 *
 * Os centros são `latitudeCentro`/`longitudeCentro` da API do visualizador
 * oficial (`apihidro.sipam.gov.br/radares/`), iguais aos centros dos círculos
 * da WFS. Radar que não esteja aqui usa o centro do bbox do manifesto (que é
 * o centro do círculo).
 */
import proj4 from 'proj4';
import type { ModeloOrigem } from './geometria';

/** [lon, lat] de cada radar. */
export const CENTROS: Readonly<Record<string, readonly [number, number]>> = {
  sbbe: [-48.46128, -1.4084],
  sbbv: [-60.7002, 2.84421],
  sbcz: [-72.7697, -7.59553],
  sbmn: [-59.9914, -3.14889],
  sbmq: [-51.0969, -0.045597],
  sbpv: [-63.8838, -8.71514],
  sbsl: [-44.2393, -2.60048],
  sbsn: [-54.799, -2.42964],
  sbtf: [-64.6932, -3.3727],
  sbtt: [-69.935, -4.24839],
  sbua: [-67.057, -0.143677],
};

export interface Sistema {
  /** Tamanho do pixel no chão (m). */
  pixelM: number;
  /** Figura da Terra na definição proj4. */
  terra: string;
}

/** Sistema de cada tamanho de PNG. Tamanho desconhecido: o dos 954 px (pixel ~0,5 km nos dois). */
export function sistemaDoPng(largura: number): Sistema {
  if (largura === 1000) return { pixelM: 500, terra: '+ellps=WGS84' };
  return { pixelM: 502.0545073375262, terra: '+R=6371000' };
}

/** Centro do radar: a tabela, ou o centro do bbox do manifesto. */
export function centroDoRadar(id: string, extent: readonly [number, number, number, number]): [number, number] {
  const c = CENTROS[id];
  return c ? [c[0], c[1]] : [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];
}

/** Modelo AEQD do PNG de um radar (`px` = tamanho do PNG). */
export function modeloAeqd(centro: readonly [number, number], px: readonly [number, number]): ModeloOrigem {
  const [lon0, lat0] = centro;
  const { pixelM, terra } = sistemaDoPng(px[0]);
  const meiaX = (px[0] * pixelM) / 2;
  const meiaY = (px[1] * pixelM) / 2;
  const p = proj4('EPSG:4326', `+proj=aeqd +lat_0=${lat0} +lon_0=${lon0} ${terra} +units=m +no_defs`);
  return {
    paraUV: (lon, lat) => {
      const [x, y] = p.forward([lon, lat]);
      return [(x + meiaX) / (2 * meiaX), (meiaY - y) / (2 * meiaY)];
    },
    deUV: (u, v) => p.inverse([u * 2 * meiaX - meiaX, meiaY - v * 2 * meiaY]) as [number, number],
  };
}

export function modeloDoRadar(
  id: string,
  extent: readonly [number, number, number, number],
  px: readonly [number, number],
): ModeloOrigem {
  return modeloAeqd(centroDoRadar(id, extent), px);
}
